import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useMemo } from 'react'
import { usePortalQuery } from 'src/Libs/api'
import { ApiAccountRole } from 'src/Libs/api/types'
import { getAccountLinkList } from 'src/Libs/installation-details/api'
import { AccountLinkModel } from 'src/Libs/installation-rights-management/models'
import { CACHE_TIME } from 'src/Libs/installations/constants'
import { useHagerUsersDetails } from 'src/Libs/user/hooks'
import { InstallerWithAccessData } from '../models'
import { installersToModelTransformer } from '../transformers'

export type UseAttachedInstallers = {
  isLoading: boolean
  isError: boolean
  data: InstallerWithAccessData[]
  refetch: () => void
}

/**
 * Hook for getting the individual installer users that have access to an installation.
 *
 * Used for the Flow theme, where installers are individual (Hager) users and are not grouped
 * into the "company" entity used by E3/DC. It loads the user account links and, because the
 * account link `accountId` is a UUID for Hager users, resolves each installer's numeric Portal
 * user ID and contact data from its username ({@link useHagerUsersDetails}) before merging them
 * with the access rights taken from the account links.
 */
export const useAttachedInstallers = (installationId: string | undefined, enabled = true): UseAttachedInstallers => {
  const authFetch = useAuthFetchWithResponse()

  const accountLinksQueryFunction = async (): Promise<AccountLinkModel[]> => {
    const { hasErrors, error, value } = await getAccountLinkList(`${installationId}`, authFetch, 'user')

    if (hasErrors) {
      throw error || new Error('Error fetching account links')
    }

    return value ?? []
  }

  const {
    data: accountLinksData,
    isLoading: accountLinksLoading,
    isError: accountLinksError,
    refetch,
  } = usePortalQuery({
    queryKey: ['installations', installationId, 'account-links', 'accountType=user'],
    queryFn: accountLinksQueryFunction,
    enabled: !!installationId && enabled,
    staleTime: CACHE_TIME,
  })

  const installerAccountLinks = useMemo(
    () => (accountLinksData ?? []).filter(accountLink => accountLink.accountType === 'user' && accountLink.role === ApiAccountRole.INSTALLER),
    [accountLinksData]
  )

  const installerUsernames = useMemo(() => installerAccountLinks.map(accountLink => accountLink.userName).filter(Boolean), [installerAccountLinks])

  const { detailsByUsername, isLoading: installerDetailsLoading } = useHagerUsersDetails(installerUsernames)

  const data = useMemo(() => installersToModelTransformer(accountLinksData ?? [], detailsByUsername), [accountLinksData, detailsByUsername])

  return {
    data,
    isLoading: accountLinksLoading || (installerUsernames.length > 0 && installerDetailsLoading),
    isError: accountLinksError,
    refetch,
  }
}
